import { Trash2, Copy, Unlink, Camera } from "lucide-react";
import { useProjectContext } from "../../contexts/ProjectContext";

interface ConfigBoxToolbarProps {
  onDuplicate?: () => void;
  onDelete?: () => void;
  onDetachFromGroup?: () => void; // Add prop for detaching from group
  onCaptureImage?: () => void; // Add prop for capturing image
  isAdvancedMode?: boolean;
  showCaptureButton?: boolean; // Control visibility of capture button
}

/**
 * A reusable toolbar component for node configuration boxes
 * Provides duplicate and delete actions in the top-right corner
 */
export default function ConfigBoxToolbar({
  onDuplicate,
  onDelete,
  onDetachFromGroup, // Destructure the new prop
  onCaptureImage, // Destructure the capture image prop
  isAdvancedMode = false,
  showCaptureButton = false, // Default to not showing the capture button
}: ConfigBoxToolbarProps) {
  const { isPreviewMode } = useProjectContext();
  
  if (!isAdvancedMode || isPreviewMode) return null;

  return (
    <div className="absolute top-0 right-0 flex space-x-1">
      {showCaptureButton && onCaptureImage && (
        <button
          onClick={onCaptureImage}
          className="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
          title="Capture Group as Image"
        >
          <Camera size={16} />
        </button>
      )}
      {onDuplicate && (
        <button
          onClick={onDuplicate}
          className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
          title="Duplicate Node"
        >
          <Copy size={16} />
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
          title="Delete Node"
        >
          <Trash2 size={16} />
        </button>
      )}
      {onDetachFromGroup && ( // Conditionally render the detach button
        <button
          onClick={onDetachFromGroup}
          className="p-1 text-gray-500 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400"
          title="Detach from Group"
        >
          <Unlink size={16} />
        </button>
      )}
    </div>
  );
}
